import { useMemo } from 'react'

type Options = {
  typesAllowed: Record<string, boolean>
}

export const useFileTypes = ({ typesAllowed }: Options) => {
  const typesAllowedEnsured = useMemo(() => {
    const defaultAllowed: Record<string, boolean> = {
      'application/pdf': true,
      'image/jpeg': true,
      'image/png': true,
      'image/gif': true,
      'application/msword': true,
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        true,
      'application/vnd.ms-excel': true,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': true,
      'application/vnd.ms-powerpoint': true,
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        true,
      'application/vnd.ms-outlook': true,
      'text/plain': true,
      'application/json': true,
      'text/csv': true,
      'image/webp': true,
      'audio/mpeg': true,
    }

    // Check if typesAllowed has entries (not an empty object)
    const hasCustomTypesAllowed = Object.keys(typesAllowed).length > 0

    // Use typesAllowed if provided, otherwise use defaultAllowed
    const allowedTypes = hasCustomTypesAllowed ? typesAllowed : defaultAllowed

    // Extract allowed keys where value is `true`
    return Object.keys(allowedTypes).filter(key => allowedTypes[key])
  }, [typesAllowed])

  const typeNames: Record<string, string> = {
    'application/pdf': 'PDF Document',
    'application/zip': 'Zip file',
    'image/jpeg': 'JPEG Image',
    'image/png': 'PNG Image',
    'image/webp': 'WebP',
    'image/gif': 'GIF Image',
    'audio/mpeg': 'MP3 Audio',
    'application/msword': 'Microsoft Word Document (.doc)',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'Microsoft Word Document (.docx)',
    'application/vnd.ms-excel': 'Microsoft Excel Spreadsheet (.xls)',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      'Microsoft Excel Spreadsheet (.xlsx)',
    'application/vnd.ms-powerpoint': 'Microsoft PowerPoint Presentation (.ppt)',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      'Microsoft PowerPoint Presentation (.pptx)',
    'application/vnd.ms-outlook': 'Microsoft Outlook Email (.msg)',
    'text/plain': 'Plain Text File',
    'application/json': 'JSON File',
    'text/csv': 'CSV File',
  }

  const getTypeFromExtensionFallback = (filename: string) => {
    const extension = filename.split('.').pop().toLowerCase()

    const mimeTypes = {
      msg: 'application/vnd.ms-outlook',
    }

    return mimeTypes[extension]
  }

  return {
    typesAllowedEnsured,
    typeNames,
    getTypeFromExtensionFallback,
  }
}
